/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useEffect, useState } from "react"
import { useGlobalState } from "../GlobalStateProvider"
import { useApi } from "../../hooks/useApi"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { IRecord } from "../../types/schema"
import { Button, Icon } from "@cd2/components/build/es6"
import { LinkButton } from "./LinkButton"
import { IconButton } from "@cd2/components"
import { icons } from "../../constants/icons"
import { DropdownMenu } from "../DropdownMenu"
import { useSettings } from "../../utils/useSettings"
import { OfficeLogo } from "../../assets/svgComponents/OfficeLogo"
import { OutlookLogo } from "../../assets/svgComponents/OutlookLogo"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"

type IUser = IRecord<"skeem_users", "microsoft_oauth"> & {
  microsoft_user_details: Record<string, any> | null
}

export interface IDynamicMessageData {
  contact?: { first_name?: string; last_name?: string }
  record?: { identifier?: string; reference?: string }
  supplier?: { name?: string; orderNumber?: string }
}

export function flattenDynamicMessageData(data?: IDynamicMessageData) {
  return {
    first_name: data?.contact?.first_name,
    last_name: data?.contact?.last_name,
    identifier: data?.record?.identifier,
    reference: data?.record?.reference,
    supplierName: data?.supplier?.name,
    orderNumber: data?.supplier?.orderNumber,
  }
}

interface IProps {
  email: IEmail
  dynamicMessageData?: IDynamicMessageData
  disabled?: boolean
  sentAtDate?: string

  afterSuccessful?(): void
}

export type IEmail = {
  body: string
  subject: string
  recipientEmailAddresses: string[]
  attachment?: {
    type: "quote" | "orderAcknowledgement" | "purchaseOrder" | "priceList" | "richQuote"
    id: string
    data?: Record<string, any>
  }
}

export function MicrosoftSignInButton({ disabled }: { disabled?: boolean }) {
  const { userDetails } = use365Mail()
  return (
    <LinkButton
      disabled={disabled}
      to={`${window.location.origin}/microsoft/auth/signin?userId=${userDetails?.id}`}
      buttonColor="white"
      buttonProps={{
        renderLeft: () => <OfficeLogo width="20px" />,
      }}
      external
    >
      Sign in to Office365
    </LinkButton>
  )
}

export function EmailWith365Button({
  email,
  disabled,
  afterSuccessful,
  dynamicMessageData,
}: IProps) {
  const { userDetails, isSending, handleSendMail, forceMsSignout, refetchUser } = use365Mail()
  const refetch = useAsyncCallback(async () => {
    await refetchUser()
  })
  if (!userDetails || disabled) {
    return (
      <div onClick={refetch}>
        <Button
          buttonColor="white"
          buttonProps={{
            renderLeft: () => <OutlookLogo width="20px" />,
          }}
          disabled
        >
          Send to Outlook
        </Button>
      </div>
    )
  }

  if (!userDetails.microsoft_oauth || Object.keys(userDetails.microsoft_oauth as any).length <= 1) {
    return <MicrosoftSignInButton />
  }

  return (
    <Button
      buttonColor="white"
      onClick={() => handleSendMail({ email, afterSuccessful, dynamicMessageData })}
      buttonProps={{
        renderLeft: () => (
          <OutlookLogo
            width="20px"
            css={css`
              margin-right: 6px;
            `}
          />
        ),
        renderRight: isSending
          ? () => <Icon icon={icons.spinner} spin />
          : () => (
              <div onClick={e => e.stopPropagation()}>
                <DropdownMenu items={[{ children: "Sign out", onClick: forceMsSignout }]}>
                  <IconButton
                    icon={{ icon: icons.settings, fixedWidth: true }}
                    buttonStyle="text"
                    padding={4}
                    color="#666"
                    square
                  />
                </DropdownMenu>
              </div>
            ),
      }}
      extendTheme={{ padding: { topAndBottom: 5, sides: 6, innerSides: 8 } }}
      disabled={isSending}
    >
      Send to Outlook <br />
      <span
        css={css`
          font-size: 12px;
          opacity: 0.7;
        `}
      >
        Signed in as {(userDetails.microsoft_user_details as any).userPrincipalName}
      </span>
    </Button>
  )
}

export function use365Mail() {
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const { quoteEmailContent, orderAcknowledgementEmailContent, purchaseOrderEmailContent } =
    useSettings()
  const [userDetails, setUserDetails] = useState<IUser | null>(null)
  const [isSending, setIsSending] = useState(false)
  const api = useApi()
  const alerts = useSiteAlerts()

  useEffect(() => {
    if (currentUser.id) {
      setUserDetails(currentUser)
    }
  }, [currentUser?.id])

  const fetchUser = useAsyncCallback(async () => {
    console.log("MS email fetch user")
    const user = await skeem.fetch2<IUser>({
      first: {
        name: "skeem_users",
        attributes: ["microsoft_user_details", "microsoft_oauth"],
        filter: { matches: { id: currentUser.id } },
      },
    })
    setUserDetails(user)
  })

  async function handleSendMail({
    email,
    afterSuccessful,
    dynamicMessageData,
    additionalData,
  }: {
    email: IEmail
    afterSuccessful?(): void
    dynamicMessageData?: IDynamicMessageData
    additionalData?: {
      includeQuoteAttachments?: boolean
    } | null
  }) {
    if (!currentUser.id) {
      throw new Error("No current user")
    }

    setIsSending(true)
    let body = email.body
    const replacerContent =
      email.attachment?.type === "quote"
        ? quoteEmailContent
        : email.attachment?.type === "orderAcknowledgement"
        ? orderAcknowledgementEmailContent
        : email.attachment?.type === "purchaseOrder"
        ? purchaseOrderEmailContent
        : undefined
    if (replacerContent) {
      body = dynamicMessageContents(replacerContent, flattenDynamicMessageData(dynamicMessageData))
    }
    try {
      const response = await api.send365Mail({
        userId: currentUser.id,
        ...email,
        body,
        additionalData,
      })
      if (response.success) {
        alerts.genericSuccess("Mail sent to drafts")
        afterSuccessful?.()
      } else {
        if (response.error) {
          if (response.error === "No oauth session") {
            window.location.href = `/microsoft/auth/signin?userId=${currentUser.id}`
          } else {
            console.error(response)
            alerts.genericError(
              typeof response.error === "string" ? response.error : JSON.stringify(response.error),
            )
          }
        } else {
          alerts.genericError("Unknown error")
        }
      }
    } catch (e) {
      console.error(e)
      alerts.genericError(JSON.stringify(e))
    } finally {
      setIsSending(false)
    }
  }

  async function forceMsSignout() {
    if (!userDetails) {
      return
    }
    await skeem.mutate({
      skeem_users: {
        update: {
          id: userDetails.id,
          microsoft_oauth: {},
          microsoft_user_details: {},
        },
      },
    })
    await fetchUser()
    alerts.genericSuccess("Signed out")
  }

  const isSignedInWith365 =
    userDetails &&
    userDetails.microsoft_oauth &&
    Object.keys(userDetails.microsoft_oauth as any).length > 1

  return {
    userDetails,
    isSending,
    handleSendMail,
    forceMsSignout,
    isSignedInWith365,
    refetchUser: fetchUser,
  }
}

export function dynamicMessageContents(
  message: string,
  data: Partial<{
    first_name: string
    last_name: string
    identifier: string
    reference: string
    supplierName: string
    orderNumber: string
  }>,
) {
  let regexedMessage = message
  Object.keys(data).forEach(variableName => {
    regexedMessage = regexedMessage.replace(
      new RegExp(`{{${variableName}}}`, "g"),
      data[variableName as keyof typeof data] || `{{${variableName}}}`,
    )
  })
  return regexedMessage
}
